// src/pages/TrainingDetailPage.jsx
import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Paper,
  Button,
  Stack,
  Chip,
  CircularProgress,
} from "@mui/material";
import { Package, ArrowLeft, Sparkles } from "lucide-react";
import DOMPurify from "dompurify";

import { getTrainingBySlug } from "../api/trainings";
import PageShell from "../components/layout/PageShell";
import ContactSection from "../components/shared/ContactSection";
import { TrainingThumb, formatPrice } from "../features/training/trainingShared";

function FormattedDescription({ html }) {
  if (!html) return null;

  const clean = DOMPurify.sanitize(
    html
      .replace(/&nbsp;/gi, " ")
      .replace(/\u00A0/g, " "),
    {
      ALLOWED_TAGS: [
        "p", "br", "strong", "b", "em", "i",
        "u", "ul", "ol", "li", "h1", "h2",
        "h3", "h4", "a",
      ],
      ALLOWED_ATTR: ["href", "target", "rel"],
    }
  );

  return (
    <Box
      sx={{
        fontSize: 15.5,
        color: "#334155",
        lineHeight: 2.1,
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        textAlign: "justify",

        "& p": {
          margin: "0 0 18px",
          textAlign: "justify",
          lineHeight: 2.1,
        },

        "& p:last-child": {
          marginBottom: 0,
        },

        "& h1, & h2, & h3, & h4": {
          color: "#071B3A",
          fontWeight: 700,
          lineHeight: 1.4,
          margin: "24px 0 12px",
        },

        "& ul, & ol": {
          paddingLeft: "24px",
          margin: "12px 0 18px",
        },

        "& li": {
          marginBottom: "8px",
        },

        "& strong, & b": {
          fontWeight: 700,
          color: "#0B1530",
        },

        "& a": {
          color: "#245CC5",
          textDecoration: "underline",
        },
      }}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}

export default function TrainingDetailPage() {
  const { slug } = useParams();

  const {
    data: training,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["training", slug],
    queryFn: () =>
      getTrainingBySlug(slug).then((r) => r.data?.data ?? r.data),
    enabled: Boolean(slug),
  });

  const enrollHref = training
    ? `/enroll?training=${encodeURIComponent(
        training.slug || slug
      )}&title=${encodeURIComponent(training.title || "")}`
    : `/enroll?training=${encodeURIComponent(slug || "")}`;

  const priceLabel = training
    ? formatPrice(training.price, training.currency)
    : null;

  const description =
    training?.short_description ||
    training?.shortDescription ||
    training?.excerpt ||
    training?.description ||
    "";

  const image =
    training?.image ||
    training?.thumbnail ||
    training?.banner ||
    training?.cover_image ||
    "";

  return (
    <PageShell>
      <Box sx={{ bgcolor: "#F7F9FC", minHeight: "100vh" }}>
        {/* TOP BLUE HERO SECTION */}
        <Box
          sx={{
            background:
              "linear-gradient(115deg, #071A38 0%, #123B75 60%, #245CC5 100%)",
            color: "#fff",
            pt: { xs: 3, md: 4 },
            pb: { xs: 12, md: 16 },
          }}
        >
          <Container maxWidth="lg">
            <Breadcrumbs
              separator="/"
              sx={{
                mb: 3,
                color: "#D5E4FF",
                "& .MuiBreadcrumbs-separator": {
                  color: "#91B6F5",
                },
              }}
            >
              <MuiLink
                component={Link}
                to="/"
                underline="none"
                sx={{ color: "inherit", fontSize: 13 }}
              >
                Home
              </MuiLink>

              <MuiLink
                component={Link}
                to="/trainings"
                underline="none"
                sx={{ color: "inherit", fontSize: 13 }}
              >
                Trainings
              </MuiLink>

              <Typography
                sx={{
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {training?.title || "Training Details"}
              </Typography>
            </Breadcrumbs>

            {isLoading ? (
              <Box sx={{ py: 5, textAlign: "center" }}>
                <CircularProgress sx={{ color: "#fff" }} />
              </Box>
            ): isError || !training ? (
              <Typography variant="h5">Training not found.</Typography>
            ) : (
              <>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    mb: 2,
                    flexWrap: "wrap",
                    rowGap: 1,
                  }}
                >
                  <Chip
                    label={training.is_bestseller ? "BESTSELLER" : "TRAINING"}
                    size="small"
                    sx={{
                      color: "#fff",
                      bgcolor: "rgba(255,255,255,0.17)",
                      fontWeight: 700,
                    }}
                  />

                  {training.training_type && (
                    <Chip
                      icon={<Sparkles size={15} />}
                      label={training.training_type.toUpperCase()}
                      size="small"
                      sx={{
                        bgcolor: "#fff",
                        color: "#10264A",
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Stack>

                <Typography
                  sx={{
                    fontSize: { xs: "2.2rem", md: "3.8rem" },
                    fontWeight: 800,
                    lineHeight: 1.15,
                    mb: 1.5,
                    overflowWrap: "anywhere",
                  }}
                >
                  {training.title}
                </Typography>

                {description && (
                  <Typography
                    sx={{
                      fontSize: { xs: 15, md: 19 },
                      color: "#DCE9FF",
                      maxWidth: 780,
                      lineHeight: 1.7,
                      mb: 2,
                      textAlign: "justify",
                    }}
                  >
                    {description}
                  </Typography>
                )}
              </>
            )}
          </Container>
        </Box>

        {/* TRAINING IMAGE AND CONTENT */}
        {training && !isLoading && (
          <Container
            maxWidth="lg"
            sx={{
              mt: { xs: -7, md: -10 },
              pb: 8,
              position: "relative",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(0, 1.8fr) minmax(300px, 0.9fr)",
                },
                gap: { xs: 3, md: 4.5 },
                alignItems: "start",
              }}
            >
              {/* LEFT COLUMN */}
              <Box sx={{ minWidth: 0 }}>
                {/* IMAGE WITH WHITE BORDER */}
                <Paper
                  elevation={0}
                  sx={{
                    overflow: "hidden",
                    borderRadius: "26px",
                    border: "4px solid #FFFFFF",
                    boxShadow:
                      "0 20px 45px rgba(8, 25, 55, 0.18)",
                    bgcolor: "#FFFFFF",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: { xs: 240, sm: 340, md: 420 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      bgcolor: "#FFFFFF",
                    }}
                  >
                    {image ? (
                      <Box
                        component="img"
                        src={image}
                        alt={training.title}
                        sx={{
                          display: "block",
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <TrainingThumb training={training} />
                    )}
                  </Box>
                </Paper>

                {/* TRAINING CONTENT & DETAILS */}
                <Paper
                  elevation={0}
                  sx={{
                    mt: 3.5,
                    p: { xs: 2.5, md: 4 },
                    borderRadius: "22px",
                    border: "1px solid #E1E8F2",
                    bgcolor: "#FFFFFF",
                    boxShadow:
                      "0 12px 30px rgba(15, 35, 65, 0.05)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: 25, md: 32 },
                      fontWeight: 800,
                      color: "#0B1D3A",
                      mb: 2,
                    }}
                  >
                    {training.title}
                  </Typography>

                  <Box sx={{ mb: 4 }}>
                    <Typography
                      sx={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: "#071B3A",
                        mb: 2,
                        pl: 1.5,
                        borderLeft: "4px solid #2D8CFF",
                        lineHeight: 1.4,
                      }}
                    >
                      About this training
                    </Typography>

                    <FormattedDescription
                      html={
                        training.long_description ||
                        training.longDescription ||
                        training.description ||
                        description
                      }
                    />
                  </Box>

                  {training.objectives && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        sx={{
                          fontSize: 22,
                          fontWeight: 700,
                          color: "#071B3A",
                          mb: 2,
                          pl: 1.5,
                          borderLeft: "4px solid #2D8CFF",
                          lineHeight: 1.4,
                        }}
                      >
                        Training objectives
                      </Typography>

                      <FormattedDescription html={training.objectives} />
                    </Box>
                  )}

                  <MuiLink
                    component={Link}
                    to="/trainings"
                    underline="none"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 2,
                      fontWeight: 700,
                      color: "#245CC5",
                    }}
                  >
                    <ArrowLeft size={17} />
                    Back to trainings
                  </MuiLink>
                </Paper>
              </Box>

              {/* RIGHT COLUMN */}
              <Stack
                spacing={2.5}
                sx={{
                  position: "sticky",
                  top: "110px",
                  alignSelf: "flex-start",
                }}
              >
                {/* BLUE ACTION CARD */}
                <Paper
                  elevation={0}
                  sx={{
                    position: "relative",
                    overflow: "hidden",
                    p: { xs: 3, md: 3 },
                    borderRadius: "26px",
                    color: "#fff",
                    background:
                      "linear-gradient(145deg, #173F91 0%, #2865E8 100%)",
                    boxShadow:
                      "0 18px 35px rgba(25, 70, 155, 0.25)",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      width: 130,
                      height: 130,
                      borderRadius: "50%",
                      bgcolor: "rgba(255,255,255,0.13)",
                      top: -65,
                      right: -35,
                      pointerEvents: "none",
                    },
                  }}
                >
                  <Box sx={{ position: "relative", zIndex: 1 }}>
                    <Package size={30} />

                    <Typography
                      sx={{
                        fontSize: 25,
                        fontWeight: 800,
                        mt: 2,
                        mb: 1,
                      }}
                    >
                      Interested in this training?
                    </Typography>

                    <Typography
                      sx={{
                        color: "#E3ECFF",
                        fontSize: 14,
                        lineHeight: 1.7,
                        mb: 3,
                      }}
                    >
                      Contact our team to learn more about this training
                      and find the right solution for your requirements.
                    </Typography>

                    <Button
                      component={Link}
                      to={enrollHref}
                      fullWidth
                      variant="contained"
                      sx={{
                        py: 1.5,
                        borderRadius: "13px",
                        bgcolor: "#FFFFFF",
                        color: "#10264A",
                        fontWeight: 800,
                        textTransform: "none",
                        boxShadow: "none",
                        mb: 1.5,
                        "&:hover": {
                          bgcolor: "#EAF1FF",
                          boxShadow: "none",
                        },
                      }}
                    >
                      Enroll Now
                    </Button>

                    <Button
                      component={Link}
                      to="/trainings"
                      fullWidth
                      sx={{
                        py: 1,
                        color: "#FFFFFF",
                        fontWeight: 600,
                        textTransform: "none",
                        "&:hover": {
                          bgcolor: "rgba(255,255,255,0.1)",
                        },
                      }}
                    >
                      Explore other trainings
                    </Button>
                  </Box>
                </Paper>

                {/* TRAINING INFORMATION CARD */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "22px",
                    bgcolor: "#FFFFFF",
                    border: "1px solid #E1E8F0",
                    boxShadow:
                      "0 12px 30px rgba(15, 35, 65, 0.06)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 19,
                      fontWeight: 800,
                      color: "#0B1D3A",
                      mb: 2.5,
                    }}
                  >
                    Training details
                  </Typography>

                  {[
                    {
                      label: "Category",
                      value:
                        training.category?.name ||
                        training.category_name ||
                        "Training",
                    },
                    {
                      label: "Training type",
                      value: training.training_type || "Classroom",
                    },
                    {
                      label: "Duration",
                      value: training.duration || "Contact us",
                    },
                    {
                      label: "Availability",
                      value: training.is_bestseller
                        ? "Bestseller"
                        : "Available",
                    },
                  ].map((detail) => (
                    <Box
                      key={detail.label}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        py: 1.5,
                        borderBottom: "1px dashed #E1E8F0",
                        "&:last-child": {
                          borderBottom: 0,
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#475569",
                          fontSize: 14,
                        }}
                      >
                        {detail.label}
                      </Typography>

                      <Typography
                        sx={{
                          color: "#245CC5",
                          bgcolor: "#EDF4FF",
                          px: 1.3,
                          py: 0.6,
                          borderRadius: "8px",
                          fontSize: 12.5,
                          fontWeight: 600,
                          textAlign: "right",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {detail.value}
                      </Typography>
                    </Box>
                  ))}

                  {priceLabel && (
                    <Box
                      sx={{
                        mt: 2.5,
                        pt: 2,
                        borderTop: "1px solid #E1E8F0",
                      }}
                    >
                      <Typography
                        sx={{ color: "#475569", fontSize: 14 }}
                      >
                        Training fee
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.5,
                          color: "#0B1D3A",
                          fontSize: 22,
                          fontWeight: 800,
                        }}
                      >
                        {priceLabel}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Stack>
            </Box>
          </Container>
        )}

        <Box sx={{ mt: 8 }}>
          <ContactSection />
        </Box>
      </Box>
    </PageShell>
  );
}